<template>
  <component
    :is="tag"
    class="base-card"
    :class="cardClass"
    v-bind="$attrs"
    v-on="$listeners"
  >
    <!-- Header -->
    <div v-if="title || $slots.header" class="card-header">
      <slot name="header">
        <h4 v-if="title" class="card-title">{{ title }}</h4>
        <p v-if="subtitle" class="card-subtitle">{{ subtitle }}</p>
      </slot>
    </div>

    <!-- Body -->
    <div class="card-body" :class="bodyClass">
      <slot></slot>
    </div>

    <!-- Footer -->
    <div v-if="$slots.footer" class="card-footer">
      <slot name="footer"></slot>
    </div>
  </component>
</template>

<script>
export default {
  name: 'BaseCard',

  inheritAttrs: false,

  props: {
    tag: {
      type: String,
      default: 'div'
    },
    title: {
      type: String,
      default: ''
    },
    subtitle: {
      type: String,
      default: ''
    },
    variant: {
      type: String,
      default: 'default',
      validator: (value) => ['default', 'elevated', 'outlined', 'flat'].includes(value)
    },
    padding: {
      type: String,
      default: 'md',
      validator: (value) => ['none', 'sm', 'md', 'lg'].includes(value)
    },
    hoverable: {
      type: Boolean,
      default: false
    },
    clickable: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    cardClass() {
      return [
        `card--${this.variant}`,
        `card--padding-${this.padding}`,
        {
          'card--hoverable': this.hoverable,
          'card--clickable': this.clickable
        }
      ]
    },
    bodyClass() {
      return {
        'card-body--no-header': !this.title && !this.$slots.header
      }
    }
  }
}
</script>

<style scoped>
.base-card {
  background: var(--card-bg, #ffffff);
  border-radius: var(--card-radius, 0.75rem);
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Variants */
.card--default {
  border: var(--card-border, 1px solid #e4e4e7);
}

.card--elevated {
  border: none;
  box-shadow: var(--shadow-md);
}

.card--outlined {
  border: 2px solid var(--border-color, #e4e4e7);
}

.card--flat {
  border: none;
  background: var(--bg-muted, #f4f4f5);
}

/* Padding Variants */
.card--padding-none .card-body {
  padding: 0;
}

.card--padding-sm .card-body {
  padding: var(--space-4, 1rem);
}

.card--padding-md .card-body {
  padding: var(--card-padding, 1.5rem);
}

.card--padding-lg .card-body {
  padding: var(--space-8, 2rem);
}

/* Header */
.card-header {
  padding: var(--card-padding, 1.5rem);
  padding-bottom: 0;
}

.card--padding-sm .card-header {
  padding: var(--space-4, 1rem);
  padding-bottom: 0;
}

.card--padding-lg .card-header {
  padding: var(--space-8, 2rem);
  padding-bottom: 0;
}

.card-title {
  font-size: var(--text-xl, 1rem);
  font-weight: var(--font-semibold, 600);
  color: var(--text-primary, #18181b);
  margin: 0;
}

.card-subtitle {
  font-size: var(--text-md, 0.875rem);
  color: var(--text-muted, #71717a);
  margin: var(--space-1, 0.25rem) 0 0 0;
}

/* Body */
.card-body--no-header {
  /* No adjustments needed when there's no header */
}

/* Footer */
.card-footer {
  padding: var(--card-padding, 1.5rem);
  padding-top: 0;
  margin-top: auto;
}

.card--padding-sm .card-footer {
  padding: var(--space-4, 1rem);
  padding-top: 0;
}

.card--padding-lg .card-footer {
  padding: var(--space-8, 2rem);
  padding-top: 0;
}

/* Interactive States */
.card--hoverable {
  transition: transform var(--transition-base, 0.2s), box-shadow var(--transition-base, 0.2s), border-color var(--transition-base, 0.2s);
}

.card--hoverable:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
}

.card--clickable {
  cursor: pointer;
  transition: transform var(--transition-base, 0.2s), box-shadow var(--transition-base, 0.2s), border-color var(--transition-base, 0.2s);
}

.card--clickable:hover {
  border-color: var(--color-primary-500, #0ea5e9);
  box-shadow: var(--shadow-md);
}

.card--clickable:active {
  transform: translateY(0);
}

/* Responsive */
@media (max-width: 576px) {
  .card--padding-md .card-body,
  .card--padding-md .card-header,
  .card--padding-md .card-footer {
    padding: var(--card-padding-sm, 1.25rem);
  }

  .card--padding-md .card-header,
  .card--padding-md .card-footer {
    padding-bottom: 0;
    padding-top: 0;
  }
}
</style>
